
import { supabase } from '../../supabase';

/**
 * Clean usage statistics based on age
 * @param timeframe - Option for cleanup: 'all', 'day', 'week', 'month', 'quarter', 'halfYear', 'year'
 * @returns Promise<number> - Number of deleted records
 */
export const cleanUsageStatistics = async (timeframe: string = 'halfYear'): Promise<number> => {
  try {
    // First, count how many records will be deleted
    let countQuery = supabase
      .from('usage_statistics')
      .select('*', { count: 'exact', head: true });
    
    // Apply timestamp filter for all options except 'all'
    if (timeframe !== 'all') {
      const cutoffDate = new Date();
      
      // Calculate cutoff date based on timeframe
      switch (timeframe) {
        case 'day':
          cutoffDate.setDate(cutoffDate.getDate() - 1);
          break;
        case 'week':
          cutoffDate.setDate(cutoffDate.getDate() - 7);
          break;
        case 'month':
          cutoffDate.setMonth(cutoffDate.getMonth() - 1);
          break;
        case 'quarter':
          cutoffDate.setMonth(cutoffDate.getMonth() - 3);
          break;
        case 'halfYear':
          cutoffDate.setMonth(cutoffDate.getMonth() - 6);
          break;
        case 'year':
          cutoffDate.setFullYear(cutoffDate.getFullYear() - 1);
          break;
        default:
          cutoffDate.setMonth(cutoffDate.getMonth() - 6); // Default to 6 months
      }

      countQuery = countQuery.lt('created_at', cutoffDate.toISOString());
    }
    
    // Execute the count query
    const { count, error: countError } = await countQuery;
    if (countError) throw countError;
    
    // If no records to delete, return early
    if (!count || count === 0) return 0;
    
    // Setup the delete query
    let deleteQuery = supabase.from('usage_statistics').delete();
    
    // Apply the same filter for deletion
    if (timeframe !== 'all') {
      const cutoffDate = new Date();
      
      // Calculate cutoff date based on timeframe (duplicate logic for clarity)
      switch (timeframe) {
        case 'day':
          cutoffDate.setDate(cutoffDate.getDate() - 1);
          break;
        case 'week':
          cutoffDate.setDate(cutoffDate.getDate() - 7);
          break;
        case 'month':
          cutoffDate.setMonth(cutoffDate.getMonth() - 1);
          break;
        case 'quarter':
          cutoffDate.setMonth(cutoffDate.getMonth() - 3);
          break;
        case 'halfYear':
          cutoffDate.setMonth(cutoffDate.getMonth() - 6);
          break;
        case 'year':
          cutoffDate.setFullYear(cutoffDate.getFullYear() - 1);
          break;
        default:
          cutoffDate.setMonth(cutoffDate.getMonth() - 6); // Default to 6 months
      }
      
      deleteQuery = deleteQuery.lt('created_at', cutoffDate.toISOString());
    }
    
    // Execute the delete query
    const { error } = await deleteQuery;
    if (error) throw error;
    
    // Return the number of deleted records
    return count || 0;
  } catch (error) {
    console.error('Error cleaning usage statistics:', error);
    throw error;
  }
};

/**
 * Clean old usage statistics (for backward compatibility)
 * @param months - Number of months to keep data for (default: 6)
 */
export const cleanOldUsageStatistics = async (months: number = 6): Promise<number> => {
  // Use the new function with the appropriate timeframe
  const timeframe = months === 6 ? 'halfYear' : 'year';
  return cleanUsageStatistics(timeframe);
};
